import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { AWAssetDto } from '@api/Api';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import {
  ApprovalRoutingIcon,
  ArrowDownIcon,
  CampaingIcon,
  ClockIcon,
  LockIcon
} from '@icons';
import './AssetModalHead.less';

type AssetModalHeadProps = {
  asset: AWAssetDto;
  approvalWorkflowName: string | null;
  approvalWorkflowId: string;
  campaignId: string;
  campaignName: string;
  workspaceId: string;
  isPastDueDateCurrentStage: boolean;
  onClose: () => void;
};

export default function AssetModalHead({
  asset,
  approvalWorkflowName,
  approvalWorkflowId,
  campaignId,
  campaignName,
  workspaceId,
  isPastDueDateCurrentStage,
  onClose
}: AssetModalHeadProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const preparePreview = useMemo(() => {
    return assetPreviewThumbnails({
      url: asset?.previewUrl || '',
      status: 'ready',
      type: asset.type,
      size: 45,
      format: 'img'
    });
  }, [asset]);

  const onOpenAsset = () => {
    onClose();
    const assetUrl = asset.hasAccess
      ? `/media-viewer/${asset.id}/${asset.versionId}`
      : `/approval?id=${approvalWorkflowId}&assetId=${asset.id}`;
    navigate(assetUrl, {
      state: {
        returnTo: location.pathname + location.search
      }
    });
  };

  return (
    <div className="approval-asset-modal_head">
      <div
        className={classNames('approval-asset-modal_head-image', {
          'approval-asset-modal_head-image--disabled': asset.accessDisabled
        })}
      >
        <div className="approval-asset-modal_head-image-lock-icon">
          <LockIcon />
        </div>
        {typeof preparePreview === 'string' ? (
          <img src={preparePreview || ''} alt="" />
        ) : (
          preparePreview
        )}
      </div>
      <div className="approval-asset-modal_head-content">
        <div className="approval-asset-modal_head-info">
          <Tooltip
            title="Open in media viewer"
            overlayClassName="toggle_favorite_overlay"
          >
            <div
              onClick={onOpenAsset}
              className="approval-asset-modal_head-name"
            >
              {asset.name}
              <span>.{asset.extension}</span>
            </div>
          </Tooltip>
          <div className="approval-asset-modal_head-version">
            V{asset.versionsCount}
          </div>
          {isPastDueDateCurrentStage && (
            <div className="approval-asset-modal_head-label">
              <ClockIcon />
              Overdue
            </div>
          )}
        </div>

        <div className="approval-asset-modal_head-breadcrumbs">
          <Tooltip
            title={`${campaignName} project`}
            overlayClassName="toggle_favorite_overlay"
            placement="top"
          >
            <Link
              className="approval-asset-modal_head-breadcrumbs-item"
              to={`/campaigns/single/${campaignId}?tab=approvals`}
              onClick={() => onClose()}
            >
              <div className="approval-asset-modal_head-breadcrumbs-icon">
                <CampaingIcon />
              </div>
              <div className="approval-asset-modal_head-breadcrumbs-name">
                {campaignName} project
              </div>
            </Link>
          </Tooltip>
          {approvalWorkflowName && (
            <>
              <div className="approval-asset-modal_head-breadcrumbs-separator">
                <ArrowDownIcon />
              </div>
              <Tooltip
                title={`Routing ${approvalWorkflowName}`}
                overlayClassName="toggle_favorite_overlay"
                placement="top"
              >
                <Link
                  className="approval-asset-modal_head-breadcrumbs-item"
                  to={`/campaigns/approvals/${workspaceId}/${campaignId}/${approvalWorkflowId}`}
                  onClick={() => onClose()}
                >
                  <div className="approval-asset-modal_head-breadcrumbs-icon">
                    <ApprovalRoutingIcon />
                  </div>
                  <div className="approval-asset-modal_head-breadcrumbs-name">
                    Routing {approvalWorkflowName}
                  </div>
                </Link>
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
